// External Dependencies
import { FC, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserAlertFlags } from '@presto-assistant/api_types';
import AccountCancelIcon from 'mdi-material-ui/AccountCancel';
import AdminIcon from 'mdi-material-ui/ShieldAccount';

// Internal Dependencies
import { ConfirmationDialog } from 'components/shared';
import {
  useRemoveUserAlertFlag,
  useSetUserAlertFlag,
} from 'gql/mutations/alertFlags';
import {
  GET_USERS_BY_ALERT_FLAGS,
  getUsersByFraudAlertFlag,
} from 'gql/queries/alertFlags';
import { useIsOpen } from 'hooks/useIsOpen';
import { useIsPrestoOwner } from 'hooks/useIsPrestoOwner';
import { useSetPrimaryAdministrator } from 'hooks/api/district';
import ToolbarMoreActionsIconMenu from 'components/shared/TableDataGrid/ToolbarMoreActionsIconMenu';

// Local Dependencies
import { evictQueryCache } from '../../hooks';
import MarkAsFraudDialog from './MarkAsFraudDialog';

// Local Typings
interface Props {
  adminPortalId: string;
  authUserId: string | null;
  memberId: string;
  userFullName: string;
  variant: 'district_administrator' | 'district_assistant' | 'director';
}

// Component Definition
const PersonListItemMoreActionsIconMenu: FC<Props> = ({
  adminPortalId,
  authUserId,
  memberId,
  userFullName,
  variant,
}) => {
  const { data } = getUsersByFraudAlertFlag();

  const isPrestoOwner = useIsPrestoOwner();

  const {
    isOpen: isFlagUserConfirmationDialogOpen,
    toggleIsOpen: toggleFlagUserConfirmationDialogIsOpen,
  } = useIsOpen();
  const {
    isOpen: isSetPrimaryConfirmationDialogOpen,
    toggleIsOpen: toggleIsSetPrimaryConfirmationDialogIsOpen,
  } = useIsOpen();

  const [
    handleRemoveUserAlertFlag,
    {
      error: removeError,
      loading: isRemovingFlag,
    },
  ] = useRemoveUserAlertFlag({
    onCompleted: toggleFlagUserConfirmationDialogIsOpen,
    refetchQueries: [{ query: GET_USERS_BY_ALERT_FLAGS }],
  });

  const [
    handleSetUserAlertFlag,
    {
      error: addError,
      loading: isAddingFlag,
    },
  ] = useSetUserAlertFlag({
    onCompleted: toggleFlagUserConfirmationDialogIsOpen,
    refetchQueries: [{ query: GET_USERS_BY_ALERT_FLAGS }],
  });

  const {
    isLoading: isSettingPrimaryAdmin,
    mutate: setPrimaryAdministrator,
  } = useSetPrimaryAdministrator();

  const queryClient = useQueryClient();

  const handleConfirmSetPrimary = useCallback(() => {
    setPrimaryAdministrator({
      body: { memberId },
      params: { id: adminPortalId },
    }, {
      onSuccess: () => {
        toggleIsSetPrimaryConfirmationDialogIsOpen();
        evictQueryCache(queryClient, ['district']);
      },
    });
  }, [
    adminPortalId,
    memberId,
    queryClient,
    setPrimaryAdministrator,
    toggleIsSetPrimaryConfirmationDialogIsOpen,
  ]);

  const isLoading = isRemovingFlag || isAddingFlag;

  const isCurrentUserMarkedAsFraud = data?.usersByAlertFlag.some(
    (user) => user.id === authUserId,
  );

  const toggleFraudVerb = `${isCurrentUserMarkedAsFraud ? 'Remove' : 'Add'}`;

  const handleClick = async () => {
    if (authUserId && isCurrentUserMarkedAsFraud) {
      await handleRemoveUserAlertFlag({
        variables: { input: { userId: authUserId } },
      });
    } else if (authUserId) {
      await handleSetUserAlertFlag({
        variables: {
          input: {
            userAlertFlagId: UserAlertFlags.Fraud.toString(),
            userId: authUserId,
          },
        },
      });
    }
  };

  const fraudStatusLabel = `${toggleFraudVerb} Fraud Status`;
  const setPrimaryLabel = 'Set as Lead Admin';

  return (
    <>
      <ToolbarMoreActionsIconMenu
        iconButtonProps={{
          size: 'large',
        }}
        moreActions={[
          {
            action: toggleFlagUserConfirmationDialogIsOpen,
            icon: <AccountCancelIcon />,
            isDisabled: !isPrestoOwner || (isPrestoOwner && !authUserId),
            text: fraudStatusLabel,
          },
          ...(adminPortalId
            ? [{
              action: toggleIsSetPrimaryConfirmationDialogIsOpen,
              icon: <AdminIcon />,
              isDisabled: !authUserId || variant === 'district_administrator',
              text: setPrimaryLabel,
            }]
            : []
          ),
        ]}
      />

      <ConfirmationDialog
        confirmButtonAction={handleConfirmSetPrimary}
        description={`Are you sure you want to set ${userFullName} as the lead administrator for this admin portal?`}
        isSubmitting={isSettingPrimaryAdmin}
        onClose={toggleIsSetPrimaryConfirmationDialogIsOpen}
        open={isSetPrimaryConfirmationDialogOpen}
        title={setPrimaryLabel}
      />

      <MarkAsFraudDialog
        addError={addError}
        isAdmin
        isCurrentUserMarkedAsFraud={isCurrentUserMarkedAsFraud}
        isLoading={isLoading}
        isOpen={isFlagUserConfirmationDialogOpen}
        onDialogClose={toggleFlagUserConfirmationDialogIsOpen}
        onUpdateFraudStatus={handleClick}
        removeError={removeError}
        userFullName={userFullName}
      />
    </>
  );
};

export default PersonListItemMoreActionsIconMenu;
